import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import api from "../../../adapters/axios";
import { FaCheck, FaExternalLinkAlt, FaCog, FaChevronUp, FaChevronDown } from "react-icons/fa";
import * as XLSX from "xlsx";
import "../AlertMenu/AlertMenu.css";
import "./DragDrop.css";

type TagOpt = { value: string; label: string; origin: "wendwise" | "user" | "new" };
type SavedItem = {
  id: string;
  tags: { id: string; tagName: string }[];
  blurb: string;
  url: string;
  source: string;
  date: string;
  addedBy: string;
};
const formatDate = (isoDate: string) => {
  if (!isoDate) return "";
  // Assume isoDate is like "2025-08-28T12:34:56.000Z" or "2025-08-28"
  const [year, month, day] = isoDate.slice(0, 10).split("-");
  return `${day}/${month}/${year}`;
};

const animatedComponents = makeAnimated();
const BLOCKED_EXTENSIONS = [".exe", ".js", ".jar", ".sh", ".jsp", ".jse", ".msi", ".bat", ".cmd"];
const URL_PROTOCOL_WHITELIST = ["http:", "https:"];

function isValidHttpUrl(text: string): boolean {
  try {
    const url = new URL(text.trim());
    if (!URL_PROTOCOL_WHITELIST.includes(url.protocol)) return false;
    const lower = url.pathname.toLowerCase();
    return !BLOCKED_EXTENSIONS.some(ext => lower.endsWith(ext));
  } catch {
    return false;
  }
}

export default function DragDrop() {
  const navigate = useNavigate();

  // Collapse state
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Form states
  const [url, setUrl] = useState("");
  const [tags, setTags] = useState<TagOpt[]>([]);
  const [newTag, setNewTag] = useState("");
  const [blurb, setBlurb] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [tempTag, setTempTag] = useState("");
  const [wendwiseOptions, setWendwiseOptions] = useState<TagOpt[]>([]);
  const [userTagOptions, setUserTagOptions] = useState<TagOpt[]>([]);
  const [errors, setErrors] = useState<{ url?: string; tag?: string }>({});
  const [flashMessage, setFlashMessage] = useState("");
const [fadeOut, setFadeOut] = useState(false);

const showFlash = (msg: string) => {
  setFlashMessage(msg);
  setFadeOut(false);
  setTimeout(() => setFadeOut(true), 1500); // start fade after 1.5s
  setTimeout(() => setFlashMessage(""), 2000); // remove after fade
};

  
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  // List states
  const [items, setItems] = useState<SavedItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);

  // Filters
  const [searchBlurb, setSearchBlurb] = useState("");
  const [filterTags, setFilterTags] = useState<TagOpt[]>([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Selection
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  // Inline edit states
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editTag, setEditTag] = useState<TagOpt | null>(null);
  const [editBlurb, setEditBlurb] = useState("");
  const [editingField, setEditingField] = useState<{ itemId: string; field: "tag" | "blurb" } | null>(null);

  // Load tags
  useEffect(() => {
    async function loadTags() {
      try {
        const [wwRes, userRes] = await Promise.all([
          api.get("/tags"),
          api.get("/user-tags").catch(() => ({ data: [] })),
        ]);
        const ww: TagOpt[] = (wwRes.data || []).map((t: any) => ({
          value: t.id?.toString() ?? t.tagName,
          label: t.tagName,
          origin: "wendwise",
        }));
        const ut: TagOpt[] = (userRes.data || []).map((t: any) => ({
          value: t.id?.toString() ?? t.tagName,
          label: t.tagName,
          origin: "user",
        }));
        setWendwiseOptions(ww);
        setUserTagOptions(ut);
      } catch (err) {
        console.error(err);
      }
    }
    loadTags();
  }, []);

  // Validation
  const validateForm = () => {
    const newErrors: { url?: string; tag?: string } = {};
    if (!url.trim()) newErrors.url = "URL is required.";
    else if (!isValidHttpUrl(url)) newErrors.url = "Please enter a valid http/https URL.";
    if (tags.length !== 1) newErrors.tag = "Please select exactly one tag.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => { if (url.trim() && isValidHttpUrl(url)) setErrors(prev => ({ ...prev, url: undefined })); }, [url]);
  useEffect(() => { if (tags.length === 1) setErrors(prev => ({ ...prev, tag: undefined })); }, [tags]);

  // Fetch items
  const fetchItems = async () => {
    setLoadingItems(true);
    try {
      const params: any = {};
      if (searchBlurb.trim()) params.blurb = searchBlurb.trim();
      if (filterTags.length) params.tagIds = filterTags.map(t => t.value).join(",");
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;
      const res = await api.get("/dragdrop/items", { params });
      setItems(res.data || []);
    } catch (err) { console.error(err); } finally { setLoadingItems(false); }
  };

  useEffect(() => { const delayDebounce = setTimeout(() => fetchItems(), 300); return () => clearTimeout(delayDebounce); }, [searchBlurb, filterTags, fromDate, toDate]);

  // Drag & Paste
  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setMessage("Files are not allowed. Drop a URL.");
      e.dataTransfer.clearData();
      return;
    }
    const droppedUrl = e.dataTransfer.getData("text/uri-list") || e.dataTransfer.getData("text/plain");
    if (isValidHttpUrl(droppedUrl)) {
      setUrl(droppedUrl.trim());
      setMessage("");
    } else {
      setMessage("Only valid http/https URLs are allowed.");
    }
  }, []);

  const onPaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text");
    if (isValidHttpUrl(text)) { setUrl(text.trim()); setMessage(""); }
  }, []);

  const allowDrop = (e: React.DragEvent) => e.preventDefault();

  // Inline tag creation
  const addInlineTag = () => {
    const raw = (newTag || "").trim();
    if (!raw) return;
    if (!/^[\w\- ]{1,60}$/.test(raw)) { setMessage("Invalid tag format."); return; }
    const exists = [...wendwiseOptions, ...userTagOptions, ...tags].some(t => t.label.toLowerCase() === raw.toLowerCase());
    if (exists) { setMessage("Tag already exists/selected."); return; }
    const opt: TagOpt = { value: `new:${raw}`, label: raw, origin: "new" };
    setTags(prev => [...prev, opt]);
    setNewTag("");
    setMessage("");
  };

  const canSave = url && tags.length === 1 && isValidHttpUrl(url) && blurb.length <= 1000;

  // Save new item
  const onSave = async () => {
    if (!validateForm()) return;
  setSaving(true);
  setMessage("");
    try {
      const newTags = tags.filter(t => t.origin === "new").map(t => t.label);
      const existingTagIds = tags.filter(t => t.origin !== "new").map(t => Number(t.value));

      let createdIds: number[] = [];
      if (newTags.length) {
        try { const res = await api.post("/user-tags/bulk", { tags: newTags }); createdIds = (res.data?.ids || []).map((id: any) => Number(id)); }
        catch { for (const name of newTags) { const r = await api.post("/user-tags", { tagName: name }); createdIds.push(Number(r.data?.id)); } }
      }

      let source = "";
      try { const urlObj = new URL(url); source = urlObj.hostname.replace(/^www\./, ""); } catch { source = ""; }

      const payload: any = { blurb: blurb.trim(), url, source, user_tag_id: null, company_tag_id: null };
      const tagId = existingTagIds[0] || createdIds[0] || null;
      if (createdIds.length || tags.some(t => t.origin === "user")) payload.user_tag_id = tagId ? Number(tagId) : null;
      else payload.company_tag_id = tagId ? Number(tagId) : null;

      await api.post("/items", payload);
      showFlash("Saved!");

    setUrl(""); 
    setTags([]); 
    setBlurb("");
    fetchItems();

    // Clear flash message after 2 seconds
    setTimeout(() => setFlashMessage(""), 5000);
    } catch (err) {
      console.error(err);
      setFlashMessage("Failed to save.");
      setTimeout(() => setFlashMessage(""), 5000);
    } finally {
      setSaving(false);
    }
  };

  // Selection
  const toggleSelectAll = () => { if (selectAll) setSelectedItems([]); else setSelectedItems(items.map(i => i.id)); setSelectAll(!selectAll); };
  const toggleSelectOne = (id: string) => { setSelectedItems(prev => prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]); };
  const getFilteredItems = () => { return items.filter(i => { const matchesBlurb = searchBlurb ? i.blurb.toLowerCase().includes(searchBlurb.toLowerCase()) : true; const matchesTags = filterTags.length ? filterTags.every(ft => i.tags.some(t => t.id === ft.value || t.tagName === ft.label)) : true; return matchesBlurb && matchesTags; }); };

  // Export Excel
  const exportToExcel = () => {
    const dataToExport = selectedItems.length ? items.filter(i => selectedItems.includes(i.id)) : getFilteredItems();
    const worksheet = XLSX.utils.json_to_sheet(
      dataToExport.map(i => ({
        Tags: i.tags.map(t => t.tagName).join(", "),
        Blurb: i.blurb,
        Link: i.url,
        Source: i.source,
        "Added By": i.addedBy,
      }))
    );
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Saved Items");
    XLSX.writeFile(workbook, "saved_items.xlsx");
  };

  // Delete selected
  const deleteSelected = async () => {
    const idsToDelete = selectedItems.length ? selectedItems : getFilteredItems().map(i => i.id);
    if (!idsToDelete.length) return;
    if (!window.confirm("Are you sure you want to delete selected/filtered items?")) return;
    try { await api.delete("/dragdrop/items/bulk", { data: { ids: idsToDelete } }); setSelectedItems([]); setSelectAll(false); fetchItems(); }
    catch (err) { console.error(err); }
  };

  // Navigate
  const handleManageUserTags = () => { navigate('/user-tags'); };

  return (
    <div className="alert-page-wrapper dragdrop-page">
          {/* Flash message */}
          {flashMessage && (
  <div className={`status-msg-flash ${fadeOut ? "fade-out" : ""}`}>
    {flashMessage}
  </div>
)}

  {/* Toggle Collapse */}
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
    <h2>📂 <span className="alert-heading">Drag &amp; Drop</span></h2>
    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}> {/* Reduced gap */}
      <button 
        className="btn btn-secondary" 
        onClick={() => setIsCollapsed(prev => !prev)} 
        style={{ display: "flex", alignItems: "center", gap: "4px" }}
      >
        {isCollapsed ? <><FaChevronDown /> Expand</> : <><FaChevronUp /> Minimize</>}
      </button>

      <button
        className="btn btn-secondary"
        onClick={handleManageUserTags}
        style={{ display: "flex", alignItems: "center", gap: "4px" }}
      >
        Manage User Tags
      </button>

     
    </div>
  </div>



      {!isCollapsed && (
        <>
          {/* FORM */}
          <div className="drop-zone" onDrop={onDrop} onDragOver={allowDrop}>
            {url ? (
              <div className="url-preview">
                <span className="label">URL</span>
                <input type="text" value={url} onChange={(e) => setUrl(e.target.value)} onPaste={onPaste} />
              </div>
            ) : (
              <div className="drop-instructions">
                <p>Drag a link or URL here (or click to paste)</p>
                <input className="hidden-url-input" value={url} placeholder="Paste URL here" onPaste={onPaste} onChange={(e) => setUrl(e.target.value)} />
              </div>
            )}
          </div>
          {errors.url && <div className="error-text">{errors.url}</div>}

      {/* Tags */}
<label className="row-label">Tag (choose one)</label>
<div className="tags-controls">
  <Select
    placeholder="Wendwise tags…"
    options={wendwiseOptions}
    components={animatedComponents}
    value={tags.length && tags[0]?.origin === "wendwise" ? tags[0] : null}
    onChange={(opt) => setTags(opt ? [opt as TagOpt] : [])}
    isDisabled={tags.length > 0 && tags[0]?.origin === "user"}
    isClearable
  />
  <Select
    placeholder="Your tags…"
    options={userTagOptions}
    components={animatedComponents}
    value={tags.length && tags[0]?.origin === "user" ? tags[0] : null}
    onChange={(opt) => setTags(opt ? [opt as TagOpt] : [])}
    isDisabled={tags.length > 0 && tags[0]?.origin === "wendwise"}
    isClearable
  />
  <div className="new-tag">
    <button
      className="btn btn-primary"
      onClick={() => setIsTagModalOpen(true)}
      disabled={tags.length > 0 && tags[0]?.origin === "wendwise"}
    >
      + Create New Tag
    </button>
   
  </div>
</div>

          {/* Modal & Blurb */}
          {isTagModalOpen && (
            <div className="custom-modal-overlay" onClick={() => setIsTagModalOpen(false)}>
              <div className="custom-modal" onClick={(e) => e.stopPropagation()}>
                <div className="tag-modal-header">
                  <h3>Create New Tag</h3>
                  <button className="close-btn" onClick={() => setIsTagModalOpen(false)}>×</button>
                </div>
                <p className="tag-rules">Allowed characters: space, hyphen, underscore <br /> Maximum: 60 characters</p>
                <div className="create-tag-input">
                  <input type="text" value={tempTag} onChange={(e) => setTempTag(e.target.value)} maxLength={60} placeholder="Enter Tag" />
                  <button className="btn btn-primary" onClick={async () => {
                    if (!tempTag.trim()) return;
                    try {
                      const res = await api.post("/user-tags/bulk", { tags: [tempTag.trim()] });
                      const newId = res.data?.ids?.[0]?.toString() || tempTag.trim();
                      const newTagOpt: TagOpt = { value: newId, label: tempTag.trim(), origin: "user" };
                      setUserTagOptions(prev => [...prev, newTagOpt]);
                      setTags(prev => [...prev, newTagOpt]);
                      setTempTag("");
                      setIsTagModalOpen(false);
                    } catch { alert("Failed to create tag."); }
                  }}>Create Tag</button>
                </div>
              </div>
            </div>
          )}
          <label className="row-label">Blurb / Notes (optional)</label>
          <textarea className="blurb" placeholder="Drop or type up to 1000 characters…" value={blurb} maxLength={1000} onChange={(e) => setBlurb(e.target.value)} />
          <div className="char-count">{blurb.length}/1000</div>
          <label className="row-label">Date</label>
          <input type="date" value={today} readOnly />
          <div className="actions">
  {flashMessage && (
    <div className={`status-msg-flash ${fadeOut ? "fade-out" : ""}`}>
      {flashMessage}
    </div>
  )}
  <button className="btn btn-primary save-btn" onClick={onSave} disabled={saving}>
    {saving ? "Saving…" : "Save"}
  </button>


            {message && <span className="status-msg">{message}</span>}
          </div>
        </>
      )}

      {/* LISTING */}
      <hr style={{ margin: "20px 0" }} />
      <h3>Saved Items</h3>
      <div className="filters-row">
        <Select isMulti className="filter-tags" placeholder="Filter by Tags" options={[...wendwiseOptions, ...userTagOptions]} value={filterTags} onChange={(opts) => setFilterTags(opts as TagOpt[])} />
        <input type="text" className="filter-blurb" placeholder="Search Blurb" value={searchBlurb} onChange={(e) => setSearchBlurb(e.target.value)} />
        <div className="date-filters">
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <button className="btn btn-primary" onClick={exportToExcel}>Export Excel</button>
        <button className="btn btn-danger" onClick={deleteSelected}>Delete</button>
      </div>

      <div className="items-table-wrapper" style={{ position: "relative" }}>
        {editingField?.field === "tag" && (
          <div className="overlay-blur" onClick={() => setEditingField(null)}
            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", backdropFilter: "blur(3px)", backgroundColor: "rgba(0,0,0,0.1)", zIndex: 999 }} />
        )}
        <table className="items-table">
          <thead>
            <tr>
              <th><input type="checkbox" checked={selectAll} onChange={toggleSelectAll} /></th>
              <th>Tag</th>
              <th>Blurb</th>
              <th>Link</th>
              <th>Source</th>
              <th>Added By</th>
              <th>Date</th> {/* New column */}
            </tr>
          </thead>
          <tbody>
            {loadingItems ? (
              <tr><td colSpan={6}>Loading…</td></tr>
            ) : getFilteredItems().length ? getFilteredItems().map(item => (
              <tr key={item.id}>
                <td><input type="checkbox" checked={selectedItems.includes(item.id)} onChange={() => toggleSelectOne(item.id)} /></td>
                <td>
                  {editingField?.itemId === item.id && editingField.field === "tag" ? (
                    <div className="inline-edit-container" style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: "250px", position: "relative", zIndex: 1000 }}>
                      <Select value={editTag} onChange={(opt) => setEditTag(opt as TagOpt)} options={[...wendwiseOptions, ...userTagOptions]} isClearable menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }), control: provided => ({ ...provided, minHeight: "40px", minWidth: "250px" }), menu: provided => ({ ...provided, maxHeight: "200px" }) }} />
                      <button className="tick-btn" style={{ height: "40px" }} onClick={async () => {
                        if (!editTag) return;
                        try { await api.post(`/dragdrop/items/${item.id}/update`, { user_tag_id: editTag.origin === "user" ? Number(editTag.value) : null, company_tag_id: editTag.origin === "wendwise" ? Number(editTag.value) : null }); fetchItems(); setEditingField(null); }
                        catch (err) { console.error("Failed to update tag", err); }
                      }}><FaCheck /></button>
                    </div>
                  ) : (
                    <span style={{ cursor: "pointer" }} onClick={() => { const tagData = item.tags[0]; const foundTag = [...wendwiseOptions, ...userTagOptions].find(t => t.label === tagData?.tagName) || null; setEditTag(foundTag); setEditingField({ itemId: item.id, field: "tag" }); }}>
                      {item.tags.map(t => t.tagName).join(", ")}
                    </span>
                  )}
                </td>
                <td>
                  {editingField?.itemId === item.id && editingField.field === "blurb" ? (
                    <div className="inline-edit-container">
                      <textarea className="inline-edit-textarea" value={editBlurb} onChange={(e) => setEditBlurb(e.target.value)} rows={4} placeholder="Type your notes here..." style={{ width: "100%", minWidth: "250px", resize: "vertical", padding: "6px", fontSize: "0.9rem" }} />
                      <button className="tick-btn" onClick={async () => { try { await api.post(`/dragdrop/items/${item.id}/update`, { blurb: editBlurb }); fetchItems(); setEditingField(null); } catch (err) { console.error("Failed to update blurb", err); } }}><FaCheck /></button>
                    </div>
                  ) : (
                    <span style={{ cursor: "pointer", fontStyle: !item.blurb ? "italic" : "normal", color: !item.blurb ? "#888" : "inherit" }} onClick={() => { setEditBlurb(item.blurb || ""); setEditingField({ itemId: item.id, field: "blurb" }); }}>
                      {item.blurb || "Click to add notes"}
                    </span>
                  )}
                </td>
                <td><a href={item.url} target="_blank" rel="noreferrer"><FaExternalLinkAlt /></a></td>
                <td>{item.source}</td>
                <td>{item.addedBy}</td>
                <td>{formatDate(item.date)}</td> {/* each row */}              </tr>
            )) : <tr><td colSpan={6}>No items found</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
